/**
 * AI player implementation for Connect Four
 * Uses minimax algorithm with alpha-beta pruning
 */

import { FourInARowEngine } from './four-in-a-row-engine';
import { AIDifficulty, IAIConfig, GameStatus, PieceColor } from '../types/game-types';

export class AIPlayer {
  private config: IAIConfig;

  constructor(difficulty: AIDifficulty = 'hard') {
    this.config = {
      difficulty,
      lookAheadDepth: this.getDifficultyDepth(difficulty),
    };
  }

  /**
   * Gets the search depth based on difficulty level
   */
  private getDifficultyDepth(difficulty: AIDifficulty): number {
    switch (difficulty) {
      case 'easy':
        return 2;
      case 'medium':
        return 4;
      case 'hard':
        return 6;
      default:
        return 4;
    }
  }

  /**
   * Makes the best move for the AI player
   * @param engine - The game engine instance
   * @returns The column number to play, or -1 if no valid moves
   */
  public makeMove(engine: FourInARowEngine): number {
    const availableColumns = engine.getAvailableColumns();

    if (availableColumns.length === 0) {
      return -1; // No valid moves
    }

    // For easy difficulty, add some randomness
    if (this.config.difficulty === 'easy' && Math.random() < 0.3) {
      return this.getRandomMove(availableColumns);
    }

    // Check for immediate winning move
    const winningMove = this.findWinningMove(engine, availableColumns);
    if (winningMove !== -1) {
      return winningMove;
    }

    // Check for blocking opponent's winning move
    const blockingMove = this.findBlockingMove(engine, availableColumns);
    if (blockingMove !== -1) {
      return blockingMove;
    }

    // Use minimax for strategic play
    const bestMove = this.minimax(engine, this.config.lookAheadDepth, true, -Infinity, Infinity);
    return bestMove.column !== undefined ? bestMove.column : this.getRandomMove(availableColumns);
  }

  /**
   * Gets a random valid move
   */
  private getRandomMove(availableColumns: number[]): number {
    return availableColumns[Math.floor(Math.random() * availableColumns.length)];
  }

  /**
   * Finds an immediate winning move for the AI
   */
  private findWinningMove(engine: FourInARowEngine, availableColumns: number[]): number {
    for (const column of availableColumns) {
      const simulatedEngine = engine.simulateMove(column);
      if (simulatedEngine) {
        const gameState = simulatedEngine.getGameState();
        const isWinning = gameState.status === 'won' && gameState.winner === 2; // AI is player 2

        if (isWinning) {
          return column;
        }
      }
    }
    return -1;
  }

  /**
   * Finds a move to block the opponent's winning move
   */
  private findBlockingMove(engine: FourInARowEngine, availableColumns: number[]): number {
    // Check each available column to see if human could win there on their next turn
    for (const column of availableColumns) {
      // Check if human (player 1) would win by playing in this column
      if (this.wouldPlayerWinInColumn(engine, column, 1)) {
        return column; // AI should block this column
      }
    }
    return -1;
  }

  /**
   * Checks if a specific player would win by playing in a given column
   */
  private wouldPlayerWinInColumn(
    engine: FourInARowEngine,
    column: number,
    playerId: 1 | 2
  ): boolean {
    // Find where the piece would land
    const board = engine.getGameState().board;
    let targetRow = -1;

    for (let row = 5; row >= 0; row--) {
      // Start from bottom
      if (board[row][column] === null) {
        targetRow = row;
        break;
      }
    }

    if (targetRow === -1) return false; // Column is full

    // Check if placing a piece for this player at targetRow, column would create a win
    const pieceColor = playerId === 1 ? PieceColor.Red : PieceColor.Yellow;

    // Temporarily place piece and check win conditions
    board[targetRow][column] = pieceColor;
    const wouldWin = this.checkWinAt(board, targetRow, column, pieceColor);
    board[targetRow][column] = null; // Remove test piece

    return wouldWin;
  }

  /**
   * Checks if placing a piece at given position creates a win
   */
  private checkWinAt(
    board: (PieceColor | null)[][],
    row: number,
    col: number,
    pieceColor: PieceColor
  ): boolean {
    const directions = [
      { row: 0, col: 1 }, // horizontal
      { row: 1, col: 0 }, // vertical
      { row: 1, col: 1 }, // diagonal
      { row: 1, col: -1 }, // anti-diagonal
    ];

    for (const dir of directions) {
      let count = 1; // Count the piece we just placed

      // Check positive direction
      let r = row + dir.row;
      let c = col + dir.col;
      while (r >= 0 && r < 6 && c >= 0 && c < 7 && board[r][c] === pieceColor) {
        count++;
        r += dir.row;
        c += dir.col;
      }

      // Check negative direction
      r = row - dir.row;
      c = col - dir.col;
      while (r >= 0 && r < 6 && c >= 0 && c < 7 && board[r][c] === pieceColor) {
        count++;
        r -= dir.row;
        c -= dir.col;
      }

      if (count >= 4) return true;
    }

    return false;
  }

  /**
   * Minimax algorithm with alpha-beta pruning
   */
  private minimax(
    engine: FourInARowEngine,
    depth: number,
    isMaximizing: boolean,
    alpha: number,
    beta: number
  ): { score: number; column?: number } {
    const gameState = engine.getGameState();

    // Terminal conditions
    if (depth === 0 || gameState.status !== GameStatus.InProgress) {
      return { score: engine.evaluatePosition() };
    }

    const availableColumns = engine.getAvailableColumns();
    if (availableColumns.length === 0) {
      return { score: 0 }; // Draw
    }

    if (isMaximizing) {
      let maxScore = -Infinity;
      let bestColumn: number | undefined;

      for (const column of availableColumns) {
        const simulatedEngine = engine.simulateMove(column);
        if (simulatedEngine) {
          const result = this.minimax(simulatedEngine, depth - 1, false, alpha, beta);

          if (result.score > maxScore) {
            maxScore = result.score;
            bestColumn = column;
          }

          alpha = Math.max(alpha, maxScore);
          if (beta <= alpha) {
            break; // Alpha-beta pruning
          }
        }
      }

      return { score: maxScore, column: bestColumn };
    } else {
      let minScore = Infinity;
      let bestColumn: number | undefined;

      for (const column of availableColumns) {
        const simulatedEngine = engine.simulateMove(column);
        if (simulatedEngine) {
          const result = this.minimax(simulatedEngine, depth - 1, true, alpha, beta);

          if (result.score < minScore) {
            minScore = result.score;
            bestColumn = column;
          }

          beta = Math.min(beta, minScore);
          if (beta <= alpha) {
            break; // Alpha-beta pruning
          }
        }
      }

      return { score: minScore, column: bestColumn };
    }
  }

  /**
   * Updates AI difficulty
   */
  public setDifficulty(difficulty: AIDifficulty): void {
    this.config.difficulty = difficulty;
    this.config.lookAheadDepth = this.getDifficultyDepth(difficulty);
  }

  /**
   * Gets current AI configuration
   */
  public getConfig(): Readonly<IAIConfig> {
    return { ...this.config };
  }
}
